const CUSTOM_ASSETS_PREFIX = 'custom-assets/';

/**
 * The subset of `AgentManagerFile` this grouping needs. Kept structural rather than
 * importing the generated type so the pure function stays testable without codegen.
 */
export type AgentManagerFileLike = {
  path?: string | null;
  name?: string | null;
  size?: number | null;
};

/** One custom asset — a folder under `custom-assets/`, presented as a single row. */
export type CustomAssetGroup = {
  /** Folder name, e.g. `product-card`. */
  name: string;
  /** Folder path, e.g. `custom-assets/product-card`. */
  path: string;
  /** Summed size of every file in the folder. */
  size: number;
  files: AgentManagerFileLike[];
};

/**
 * `customAssetsFiles` returns individual files, but a row in the Assets tab is an
 * asset. Group by the first path segment under `custom-assets/`.
 *
 * Skips anything that is not a file inside an asset folder: bare directory
 * placeholders (trailing slash) and files sitting directly under `custom-assets/`,
 * which belong to no asset and would otherwise create a phantom row.
 */
export const groupCustomAssets = (
  files: (AgentManagerFileLike | null | undefined)[] | null | undefined
): CustomAssetGroup[] => {
  const groups = new Map<string, CustomAssetGroup>();

  for (const file of files ?? []) {
    const path = file?.path;
    if (!path || path.endsWith('/') || !path.startsWith(CUSTOM_ASSETS_PREFIX)) {
      continue;
    }

    const rest = path.slice(CUSTOM_ASSETS_PREFIX.length);
    const slashIndex = rest.indexOf('/');
    // No slash means the file is directly inside `custom-assets/`, not in an asset.
    if (slashIndex <= 0) continue;

    const name = rest.slice(0, slashIndex);
    const existing = groups.get(name);

    if (existing) {
      existing.files.push(file);
      existing.size += file.size ?? 0;
      continue;
    }

    groups.set(name, {
      name,
      path: `${CUSTOM_ASSETS_PREFIX}${name}`,
      size: file.size ?? 0,
      files: [file],
    });
  }

  return [...groups.values()];
};
